<template>
<div :class="(config.layout === 'desktop') ? 'with-breadcrumb' : 'without-breadcrumb'">

    <page-loading v-if="!data_loaded" />

    <div v-else class="container-fluid">
	
		<div class="row user" :class="{ 'inactive' : account.status != 1 }">

	        <div v-if="account.status != 1" class="col-12">
	            <div v-if="account.active_text == 'Invite Sent'" class="alert alert-primary" data-test="userInactiveAlert">
	            	This user has received an invitation and their account is pending. <router-link v-if="account.can.update && account.id != user.id" :to="{ name: 'users.settings', params: { id: account.id } }" class="font-weight-bold">Edit the account</router-link> to re-send the invitation.
	            </div>
	            <div v-if="account.active_text == 'Invite Expired'" class="alert alert-primary" data-test="userInactiveAlert">
	            	This user's account invitation has expired. <router-link v-if="account.can.update && account.id != user.id" :to="{ name: 'users.settings', params: { id: account.id } }" class="font-weight-bold">Edit the account</router-link> to re-send the invitation.
	            </div>
	            <div v-if="account.active_text == 'Inactive'" class="alert alert-primary" data-test="userInactiveAlert">
	            	This user account is inactive. <router-link v-if="account.can.update && account.id != user.id" :to="{ name: 'users.settings', params: { id: account.id } }" class="font-weight-bold">Edit the account</router-link> to set as active.
	            </div>	            	            
	        </div>

			<div class="col-12 col-md-6">
				<div class="card" id="profile">
	                <div class="profile-header d-flex align-items-end rounded-top text-white text-3xl">

	                    <span class="profile-name flex-grow-1 font-weight-bold mr-2 mb-2 leading-none" data-test="pageTitle">
	                    	{{ account.display_name }}
	                    </span>

	                    <div class="drop-down drop-down-show dropleft mr-35" style="z-index:10">
	                        <a href="#" role="button" id="physicianDropDown" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" data-test="contextMenu">
	                            <i class="far fa-ellipsis-h text-xl text-white"></i>
	                        </a>
							<div class="dropdown-menu dropdown-menu-right py-0" aria-labelledby="dropdownMenuLink">
								<router-link v-if="account.id === user.id" :to="{ name: 'users.edit', params: { id: account.id } }" class="dropdown-item">Edit User</router-link>
	                            <router-link v-if="account.can.update && account.id != user.id" :to="{ name: 'users.settings', params: { id: account.id } }" class="dropdown-item">Edit User</router-link>
							</div>
						</div>

						<div class="profile-image rounded-top" :style="'background-image:url(' + account.client.photo_url + ');'"></div>
					</div>

					<div class="px-35 pt-2 pb-35">
	                	<div class="mb-3" :class="$root.$mq === 'xs' || $root.$mq === 'sm' ? '' : 'd-flex'">
	                        <div class="profile-avatar" style="position: relative;">
	                            <div class="rounded-circle circle-cropped avatar-xl mr-3 bg-white">
	                                <img class="img-fluid" :src="account.photo_url" alt="Avatar" >
	                            </div>                    
	                        </div>

	                        <div class="flex-grow-1">
								<span v-for="role in account.roles" class="badge text-base bg-blue-100 text-blue-700 mr-1">{{ role.name }}</span>
	                        </div>
	                	</div>
	                </div>

				</div>

				<div class="card" id="clients">
					<div class="card-header">Clients</div>
					<div class="card-body">
						<div v-if="account.org" class="d-flex pb-2 mb-2 border-bottom">
							<div class="rounded-circle circle-cropped avatar-sm mr-2 bg-gray-100 text-center">
						    	<i class="fal fa-sitemap text-gray-500"></i>
						    </div>   
							<span class="font-weight-bold">{{ account.org.name }}</span>
							<span class="text-gray-500 ml-2" v-if="account.roles.some(role => role.name == 'org admin')">(Org admin: can access all clients)</span>							
						</div>
						<div v-for="(client, index) in account.client_list_all" class="d-flex" :class="{ 'mb-2' : account.client_list_all.length - 1 > index }">
							<div class="rounded-circle circle-cropped avatar-sm mr-2 bg-gray-100 text-center">
						    	<img v-if="client.logo_url" :src="client.logo_url" class="img-fluid">
						    	<i v-else class="fal fa-hospital text-gray-500"></i>
						    </div>   
							{{ client.name }}
						</div>
					</div>
				</div>

				<div class="card goals" id="goals">
					<div class="card-header">Active Goals for {{ user.client.name ? user.client.name : 'unknown client' }}</div>
					<div v-if="!accountGoals.length" class="text-center text-lg text-gray-500 p-35">
						No goals found.
					</div>
					<div v-else class="mt-3">
						<goal-item-small 
							v-for="(goal, key) in accountGoals"
							:goal="goal"
							:key="key"
							class="px-35"
						/>
					</div>
					<div class="card-footer">
						Add, edit, or view all  goals in the <router-link :to="{ name: 'goals.index' }">goal manager</router-link>.
					</div>
				</div>
			</div>

			<div class="col-12 col-md-6 mb-3 pb-5 pb-md-0">
				<div class="card" :class="{'pending' : !accountCheckinsLoaded}" id="checkins">
					<img v-if="!accountCheckinsLoaded" class="center-me" src="/images/loading-color.svg">

					<div class="card-header d-flex align-items-center px-35" style="padding-top:10px; padding-bottom: 9px;">
						<span>User Check-ins for {{ user.client.name ? user.client.name : 'unknown client' }} <span class="badge badge-primary text-sm ml-1">{{ accountCheckins.length }}</span></span>
						<div class="flex-grow-1 text-right">
							<span class="btn-group btn-group-sm">
								<button @click="fetchUserCheckins('mtd')" class="btn btn-light m-0" :class="{'active' : checkinRange == 'mtd'}">MTD</button>
								<button @click="fetchUserCheckins('ytd')" class="btn btn-light m-0" :class="{'active' : checkinRange == 'ytd'}">YTD</button>
								<button @click="fetchUserCheckins('all')" class="btn btn-light m-0" :class="{'active' : checkinRange == 'all'}">All</button>
							</span>
						</div>
					</div>
					<div class="card-body d-flex justify-content-between checkins-table">
						<div class="flex-grow-1 mr-3">
							<div class="mb-1 text-blue-700 font-weight-bold">By Checkin Type</div>
							<div class="mb-1" v-for="type in options.checkin_types">
								<span class="badge badge-primary text-base mr-2" :class="type.css_class_name">{{ type.label }}</span> <strong>{{ checkinTypes[type.label] ? checkinTypes[type.label] : 0 }}</strong>
							</div>
						</div>
						<div class="flex-grow-1 mr-3">
							<div class="mb-1 text-blue-700 font-weight-bold">By Service Lines</div>
							<div v-for="(count, line) in checkinLines">{{ line }} <span class="badge badge-primary ml-2">{{ count }}</span></div>
						</div>
						<div class="flex-grow-1">
							<div class="mb-1 text-blue-700 font-weight-bold">By Specialties</div>
							<div v-for="(count, spec) in checkinSpecs">{{ spec }} <span class="badge badge-primary ml-2">{{ count }}</span></div>
						</div>
					</div>

					<div class="rounded-bottom text-center bg-gray-100">
						<div v-if="!mapDisplay" class="pt-4 pb-5">
							<div class="card-header" style="border-bottom: none;">View check-in locations</div>
							<span class="btn btn-primary" @click="setMapDisplay(true)"><i class="fad fa-map-marked-alt"></i> Show Map</span>
						</div>
						
						<div v-if="mapDisplay" class="d-flex justify-content-between bg-yellow-100 text-sm text-left px-35 py-2">
							<span>
								Only check-ins stored with locations are mapped. <span v-if="this.markers.length > 250">Max 250 items shown on map.</span>
							</span>
							<span @click="setMapDisplay(false)" class="font-weight-bold cursor-pointer ml-2">Hide map</span>
						</div>
						<GmapMap
							v-if="mapDisplay"
							ref="checkinMap"
							:center=default_center
							:zoom="10"
							map-type-id="roadmap"
							style="height:350px; margin:0 0px 0px 0px;"
						/>
					</div>

				</div>
			</div>
				
		</div>
	</div>

    <section id="bottom-menu" class="d-md-none navbar fixed-bottom bg-white text-center text-sm border-top pt-1 pb-0" style="z-index:1010">
        <a class="btn btn-ghost btn-sm text-gray-700 text-sm" href="#" v-scroll-to="'#profile'">
            <i class="text-secondary fal fa-user"></i><br>
            Profile
        </a>
        <a class="btn btn-ghost btn-sm text-gray-700 text-sm" href="#" v-scroll-to="'#clients'">
            <i class="text-secondary fal fa-hospital"></i><br>
            Clients
        </a>
        <a class="btn btn-ghost btn-sm text-gray-700 text-sm" href="#" v-scroll-to="'#goals'">
            <i class="text-secondary fal fa-trophy-alt"></i><br>
            Goals
        </a>
        <a class="btn btn-ghost btn-sm text-gray-700 text-sm" href="#" v-scroll-to="'#checkins'">
            <i class="text-secondary fal fa-check-circle"></i><br>
            Checkins
        </a>
    </section>
</div>
</template>

<script>

	import { mapState } from 'vuex';
    import BreadcrumbsManager from '../../helpers/BreadcrumbManager';
	import GoalItemSmall from '../../components/GoalItemSmall';

	export default {
		mixins: [BreadcrumbsManager],

		components: {
			GoalItemSmall
		},

		props: {
			id: {
				type: Number,
				required: true,
			},
		},

        data() {
            return {
                data_loaded: false,
                
                account: null,
                accountCheckins: [],
                accountCheckinsLoaded: false,
                checkinRange: 'mtd',

                accountGoals: [],
                accountGoalsLoaded: false,

                mapDisplay: false,
                default_center: {},
				markers: [],

				icon: { //base icon styling
				    path: 'M12 0c-4.198 0-8 3.403-8 7.602 0 4.198 3.469 9.21 8 16.398 4.531-7.188 8-12.2 8-16.398 0-4.199-3.801-7.602-8-7.602zm0 11c-1.657 0-3-1.343-3-3s1.343-3 3-3 3 1.343 3 3-1.343 3-3 3z',
				    fillColor: '#EE2742',
				    fillOpacity: 1,
				    strokeWeight: 1.25,
				    strokeColor: 'rgba(255,255,255,.8)',				    
				    size: {width: 24, height: 24, f: 'px', b: 'px'},
				    anchor: {x: 12, y: 24, },
				    scale: 1.5
				},                
            }
        },

        created() {
        	this.fetchUser();
        	this.fetchUserCheckins('mtd');
        	this.fetchUserGoals();          	
        },

        activated() {
            this.setBreadcrumbs([
                { text: 'Home', to: { name: 'home' } },
                { text: 'Users', to: { name: 'users.index'} },
                { text: '[User Name]', to: { name: 'accounts.show'} },
            ]);  
        },

        methods: {
        	fetchUser() {
        		axios.get('/api/admin/users/' + this.id).then(({data}) => {
                    this.account = data;
                    this.default_center = { 
                    	lat: this.account.client.latitude,
                    	lng: this.account.client.longitude
                    };
                    this.data_loaded = true;
                    this.replaceBreadcrumb({
                        find: '[User Name]',
                        replace: { text: this.account.display_name }
                    });  	                    
	        	});
        	},

        	fetchUserGoals() {
        		axios.get('/api/goals/user/' + this.id).then(({data}) => {
        			this.accountGoals = data;
        			this.accountGoalsLoaded = true;
        		});
        	},

        	fetchUserCheckins(range) {
        		this.accountCheckinsLoaded = false;
        		axios.get('/api/checkins/user/' + this.id + '?range=' + range).then(({data}) => {
                    this.checkinRange = range;
                    this.accountCheckins = data;
                    this.accountCheckinsLoaded = true;

                    if (this.mapDisplay) {
                    	this.makeMap();
                    }
	        	});
        	},

			makeMap() {
				Vue.nextTick( () => {
					this.$refs.checkinMap.$mapPromise.then((map) => {
						if (this.accountCheckins.length > 0) {
							let bounds = new google.maps.LatLngBounds();
							//add markers to the map
							this.accountCheckins.slice(0,250).forEach(checkin => {
							    if (checkin.latitude !== null && checkin.longitude !== null) {
							    	let marker = new google.maps.Marker({
							    		position: new google.maps.LatLng(checkin.latitude, checkin.longitude),
								        icon: this.icon,
								        map: map,
								    });
								    bounds.extend(marker.position);
								    this.markers.push(marker);
								}							
							});
							map.fitBounds(bounds);
						}
					});
				});
			},

			setMapDisplay(value) {
				this.mapDisplay = value;
				if (value) {
					this.makeMap();
				}
			},
        },

        computed: {
        	...mapState(['config', 'options', 'user']),

			checkinLines() {
				if (this.accountCheckins) {
					let lines = [];
					this.accountCheckins.forEach(item => lines.push(...item.service_lines));
					return _.countBy(lines, 'label');
				}
			},

			checkinSpecs() {
				if (this.accountCheckins) {
					let specs = [];
					this.accountCheckins.forEach(item => specs.push(...item.checkinable.specialties));
					return _.countBy(specs, 'label');
				}
			},

			checkinTypes() {
				if (this.accountCheckins) {
					return _.countBy(this.accountCheckins, 'checkin_type.label');
				}
			},
        }

	}
</script>

<style scoped>
    .with-breadcrumb {
        margin-top:70px;
    }

    .without-breadcrumb {
        margin-top:20px;
    }
</style>